import React, { useState, useEffect, useCallback, useMemo } from "react";
import Sidebar from "../components/Sidebar";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { SERVER_URL } from "../config";

// ---- point this at wherever the PHP files are deployed ----

// Editable lookup lists -- adjust these to match your real business values.
const STATUS_OPTIONS = ["Open", "Follow-up", "Quoted", "Won", "Lost"];
// const CUSTOMER_TYPE_OPTIONS = ["New", "Existing"];
const USER_TYPE_OPTIONS = ["New", "Existing"];
const CUSTOMER_SOURCE_OPTIONS = ["Local","Direct"];

const STATUS_STYLES = {
  Open: "bg-sky-50 text-sky-700 ring-sky-600/20",
  "Follow-up": "bg-amber-50 text-amber-700 ring-amber-600/20",
  Quoted: "bg-violet-50 text-violet-700 ring-violet-600/20",
  Won: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  Lost: "bg-rose-50 text-rose-700 ring-rose-600/20",
};

const EMPTY_FORM = {
  enquiry_date: "",
  customer_name: "",
  status: "Open",
  customer_type: "",
  enquiry_sent_by: "",
  item_description: "",
  qty: "",
  quotation_no: "",
  user_type: "",
  principle: "",
  customer_source: "",
  sent_by: "",
};

const FIELD_LABELS = {
  enquiry_date: "Enquiry Date",
  customer_name: "Customer Name",
  customer_type: "Customer Type",
  status: "Status",
  customer_type: "Customer Type",
  enquiry_sent_by: "Enquiry Sent By",
  item_description: "Item Description",
  qty: "Quantity",
  quotation_no: "Quotation No.",
  user_type: "User Type",
  principle: "Principle",
  customer_source: "Customer Source",
  sent_by: "Sent By",
};

const TABLE_COLUMNS = [
  { key: "sl_no", label: "SL No" },
  { key: "enquiry_date", label: "Enquiry Date" },
  { key: "customer_name", label: "Customer Name" },
  { key: "status", label: "Status" },
  { key: "customer_type", label: "Customer Type" },
  { key: "enquiry_sent_by", label: "Enquiry Sent By" },
  { key: "item_description", label: "Item Description" },
  { key: "qty", label: "Qty" },
  { key: "quotation_no", label: "Quotation No." },
  { key: "user_type", label: "User Type" },
  { key: "principle", label: "Principle" },
  { key: "customer_source", label: "Customer Source" },
  { key: "sent_by", label: "Sent By" },
];

function Icon({ path, className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  );
}
const PlusIcon = (p) => <Icon {...p} path="M12 5v14M5 12h14" />;
const CloseIcon = (p) => <Icon {...p} path="M18 6L6 18M6 6l12 12" />;
const EditIcon = (p) => <Icon {...p} path="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />;

export default function EnquiryGeneration({ currentUser }) {
  const user = currentUser || "";

  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSlNo, setEditingSlNo] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const fetchEnquiries = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${SERVER_URL}/Rahul/get_enquiries.php`);
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Failed to load enquiries");
      setEnquiries(data.data || []);
    } catch (err) {
      setError(err.message || "Something went wrong while loading enquiries");
    } finally {
      setLoading(false);
    }
  }, [user.employee_id, user.privilege]);

  useEffect(() => {
    fetchEnquiries();
  }, [fetchEnquiries]);

  const openCreateModal = () => {
    setEditingSlNo(null);
    setForm({ ...EMPTY_FORM, enquiry_sent_by: user.employee_name, sent_by: user.employee_name });
    setFormError("");
    setIsModalOpen(true);
  };

  const openEditModal = (row) => {
    setEditingSlNo(row.sl_no);
    setForm({
      enquiry_date: row.enquiry_date ? row.enquiry_date.slice(0, 10) : "",
      customer_name: row.customer_name || "",
      status: row.status || "Open",
      customer_type: row.customer_type || "",
      enquiry_sent_by: row.enquiry_sent_by || "",
      item_description: row.item_description || "",
      qty: row.qty ?? "",
      quotation_no: row.quotation_no || "",
      user_type: row.user_type || "",
      principle: row.principle || "",
      customer_source: row.customer_source || "",
      sent_by: row.sent_by || "",
    });
    setFormError("");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setIsModalOpen(false);
  };

  const handleChange = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) {
      setFormError("Customer name is required.");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      const isEdit = editingSlNo !== null;
      const url = `${SERVER_URL}/Rahul/${isEdit ? "update_enquiry.php" : "add_enquiry.php"}`;
      const payload = isEdit
        ? { ...form, sl_no: editingSlNo, employee_id: user.employee_id, privilege: user.privilege }
        : form;

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Save failed");

      setIsModalOpen(false);
      fetchEnquiries();
    } catch (err) {
      setFormError(err.message || "Could not save the enquiry");
    } finally {
      setSaving(false);
    }
  };

 const visibleColumns = useMemo(() => TABLE_COLUMNS, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      {/* Page header */}
      <div className="bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-white hover:bg-slate-700 p-2 rounded-lg"
            >
              <Bars3Icon className="w-6 h-6" />
            </button>

            <div>
              <h1 className="text-2xl font-semibold text-white tracking-tight">
                Enquiry Generation
              </h1>

              <p className="text-sm text-slate-400 mt-1">
                Viewing all enquiries via the Process Automation system. You can create new enquiries or update existing ones.
              </p>
            </div>
          </div>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 active:bg-indigo-600 text-white text-sm font-medium px-4 py-2.5 transition-colors shadow-sm"
          >
            <PlusIcon className="w-4 h-4" />
            Generate Enquiry
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-sm px-4 py-3">
            {error}
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="max-h-[65vh] overflow-y-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 bg-slate-100 shadow-[0_1px_0_0_theme(colors.slate.200)]">
                <tr>
                  {visibleColumns.map((col) => (
                    <th
                      key={col.key}
                      className="text-left font-semibold text-slate-600 uppercase tracking-wide text-[11px] px-4 py-3 whitespace-nowrap"
                    >
                      {col.label}
                    </th>
                  ))}
                  <th className="text-left font-semibold text-slate-600 uppercase tracking-wide text-[11px] px-4 py-3 whitespace-nowrap">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr>
                    <td colSpan={visibleColumns.length + 1} className="px-4 py-10 text-center text-slate-400">
                      Loading enquiries…
                    </td>
                  </tr>
                )}

                {!loading && enquiries.length === 0 && (
                  <tr>
                    <td colSpan={visibleColumns.length + 1} className="px-4 py-10 text-center text-slate-400">
                      No enquiries yet. Click "Generate Enquiry" to add the first one.
                    </td>
                  </tr>
                )}

                {!loading &&
                  enquiries.map((row) => (
                    <tr key={row.sl_no} className="hover:bg-slate-50">
                      {visibleColumns.map((col) => (
                        <td key={col.key} className="px-4 py-3 text-slate-700 whitespace-nowrap">
                          {col.key === "status" ? (
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${STATUS_STYLES[row.status] || "bg-slate-50 text-slate-600 ring-slate-500/20"
                                }`}
                            >
                              {row.status || "—"}
                            </span>
                          ) : col.key === "item_description" ? (
                            <span className="block max-w-xs truncate" title={row.item_description}>
                              {row.item_description || "—"}
                            </span>
                          ) : (
                            row[col.key] ?? "—"
                          )}
                        </td>
                      ))}
                      <td className="px-4 py-3">
                        <button
                          onClick={() => openEditModal(row)}
                          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 text-slate-600 hover:text-indigo-600 text-xs font-medium px-2.5 py-1.5 transition-colors"
                        >
                          <EditIcon className="w-3.5 h-3.5" />
                          Update
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={closeModal} />
          <div className="relative bg-white w-full max-w-2xl rounded-xl shadow-xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900">
                {editingSlNo !== null ? "Update Enquiry" : "New Enquiry"}
              </h2>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-600">
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="overflow-y-auto px-6 py-5">
              {formError && (
                <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-sm px-3 py-2">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={FIELD_LABELS.enquiry_date}>
                  <input
                    type="date"
                    value={form.enquiry_date}
                    onChange={handleChange("enquiry_date")}
                    className={inputClass}
                  />
                </Field>

                <Field label={FIELD_LABELS.customer_name} required>
                  <input
                    type="text"
                    value={form.customer_name}
                    onChange={handleChange("customer_name")}
                    className={inputClass}
                    placeholder="e.g. Acme Industries"
                    required
                  />
                </Field>

                <Field label={FIELD_LABELS.status}>
                  <select value={form.status} onChange={handleChange("status")} className={inputClass}>
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </Field>

               <Field label={FIELD_LABELS.customer_type} required>
                  <input
                    type="text"
                    value={form.customer_type}
                    onChange={handleChange("customer_type")}
                    className={inputClass}
                    placeholder="e.g. Acme Industries"
                    required
                  />
                </Field>

                <Field label={FIELD_LABELS.enquiry_sent_by}>
                  <input
                    type="text"
                    value={form.enquiry_sent_by}
                    onChange={handleChange("enquiry_sent_by")}
                    className={inputClass}
                  />
                </Field>

                <Field label={FIELD_LABELS.quotation_no}>
                  <input
                    type="text"
                    value={form.quotation_no}
                    onChange={handleChange("quotation_no")}
                    className={inputClass}
                  />
                </Field>

                <Field label={FIELD_LABELS.qty}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.qty}
                    onChange={handleChange("qty")}
                    className={inputClass}
                  />
                </Field>

                <Field label={FIELD_LABELS.user_type}>
                  <select value={form.user_type} onChange={handleChange("user_type")} className={inputClass}>
                    <option value="">Select…</option>
                    {USER_TYPE_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </Field>

                <Field label={FIELD_LABELS.principle}>
                  <input
                    type="text"
                    value={form.principle}
                    onChange={handleChange("principle")}
                    className={inputClass}
                  />
                </Field>

                <Field label={FIELD_LABELS.customer_source}>
                  <select value={form.customer_source} onChange={handleChange("customer_source")} className={inputClass}>
                    <option value="">Select…</option>
                    {CUSTOMER_SOURCE_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </Field>

                <Field label={FIELD_LABELS.sent_by}>
                  <input
                    type="text"
                    value={form.sent_by}
                    onChange={handleChange("sent_by")}
                    className={inputClass}
                  />
                </Field>

                <Field label={FIELD_LABELS.item_description} full>
                  <textarea
                    rows={3}
                    value={form.item_description}
                    onChange={handleChange("item_description")}
                    className={inputClass}
                    placeholder="What is the customer enquiring about?"
                  />
                </Field>
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="text-sm font-medium text-slate-600 hover:text-slate-800 px-4 py-2.5"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 text-white text-sm font-medium px-5 py-2.5 transition-colors"
                >
                  {saving ? "Saving…" : editingSlNo !== null ? "Save Changes" : "Create Enquiry"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const inputClass =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400";

function Field({ label, required, full, children }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="block text-xs font-medium text-slate-500 mb-1.5">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
    </div>
  );
}